import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useFocus, useInteractions, useClick, useDismiss, flip, autoUpdate, size } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import Icons from '../../../../Elements/Icons/index.js';
import { ANIMATION_DURATION_MS } from './constants.js';
import { Button, ButtonWrapper, Wrapper, CaretWrapper } from './styled.js';
function selectPlacement(align) {
    switch (align) {
        case 'right':
            return 'bottom-start';
        case 'left':
            return 'bottom-end';
        default:
            return 'bottom-start';
    }
}
// A menu that has to open above the button keeps its alignment, so it extends the same way as below it. Switching the
// alignment as well would grow it over whatever sits on the other side, e.g. the console sidebar, so the two
// placements that switch it come last, for a menu that would otherwise leave the screen sideways.
const fallbackPlacements = {
    'bottom-start': ['top-start', 'bottom-end', 'top-end'],
    'bottom-end': ['top-end', 'bottom-start', 'top-start'],
};
const marginFromBottomScreenEdge = 8;
const maxMenuHeight = 375;
export const FilterButton = ({ id, className, dataTestId, children, dropdown, disabled = false, zIndex = 8, align = 'right', isShown, setIsShown, closeOnScroll = false, recover, dropdownWidth, }) => {
    const theme = useTheme();
    const floatingPlacement = useMemo(() => selectPlacement(align), [align]);
    const middleware = useMemo(() => [
        offset(5),
        flip({
            fallbackPlacements: fallbackPlacements[floatingPlacement],
        }),
        size({
            apply({ availableHeight, elements }) {
                elements.floating.style.maxHeight =
                    availableHeight < maxMenuHeight ? `${availableHeight - marginFromBottomScreenEdge}px` : `${maxMenuHeight}px`;
            },
        }),
    ], [floatingPlacement]);
    const { refs, floatingStyles, context, placement } = useFloating({
        open: isShown,
        onOpenChange: setIsShown,
        whileElementsMounted: autoUpdate,
        placement: floatingPlacement,
        middleware,
    });
    const focus = useFocus(context);
    const click = useClick(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, focus]);
    useDismiss(context, {
        enabled: isShown,
        ancestorScroll: closeOnScroll,
    });
    const isTop = useMemo(() => placement.startsWith('top'), [placement]);
    const stopPropagation = useCallbackRef((evt) => evt.stopPropagation());
    const caretColor = useMemo(() => (disabled ? theme.colors.grey30_1 : theme.colors.grey70_1), [disabled, theme.colors.grey30_1, theme.colors.grey70_1]);
    const memoizedFloatingStyles = useMemo(() => ({
        ...floatingStyles,
        zIndex,
    }), [floatingStyles, zIndex]);
    return (_jsxs(_Fragment, { children: [ReactDOM.createPortal(_jsx(CSSTransition, { in: isShown, transitionAppear: true, timeout: ANIMATION_DURATION_MS, unmountOnExit: true, children: _jsx(Wrapper, { ref: refs.setFloating, ...getFloatingProps(), style: memoizedFloatingStyles, $visible: isShown, $isTop: isTop, width: dropdownWidth, "data-testid": 'filter-content-wrapper', children: dropdown }) }), document.body), _jsx(ButtonWrapper, { onClick: stopPropagation, children: recover ? (_jsxs(Button, { className: className, type: 'button', children: [children, recover] })) : (_jsxs(Button, { id: id, className: className, "data-testid": dataTestId, disabled: disabled, 
                    // eslint-disable-next-line react-hooks/refs
                    ref: refs.setReference, ...getReferenceProps(), type: 'button', children: [children, _jsx(CaretWrapper, { children: _jsx(Icons.Caret, { size: 16, align: isShown ? 'top' : 'bottom', color: caretColor }) })] })) })] }));
};
